import React, { useState, useRef } from 'react';
import './BlockCanvas.css';

interface PlacedBlock {
    x: number;
    y: number;
    texture: string;
}

interface BlockCanvasProps {
    selectedTexture: string | null;
    onBlocksChange: (blocks: PlacedBlock[]) => void;
}

const BlockCanvas: React.FC<BlockCanvasProps> = ({ selectedTexture, onBlocksChange }) => {
    const [placedBlocks, setPlacedBlocks] = useState<Map<string, PlacedBlock>>(new Map());
    const [isMouseDown, setIsMouseDown] = useState(false);
    const [dragMode, setDragMode] = useState<'place' | 'erase' | null>(null);
    const canvasRef = useRef<HTMLDivElement>(null);

    const GRID_SIZE = 32;
    const CANVAS_WIDTH = 1024; // 32 blocks wide
    const CANVAS_HEIGHT = 1024; // 32 blocks tall
    const GRID_COLS = CANVAS_WIDTH / GRID_SIZE;
    const GRID_ROWS = CANVAS_HEIGHT / GRID_SIZE;

    const getGridPosition = (clientX: number, clientY: number) => {
        if (!canvasRef.current) return null;
        
        const rect = canvasRef.current.getBoundingClientRect();
        const x = Math.floor((clientX - rect.left) / GRID_SIZE);
        const y = Math.floor((clientY - rect.top) / GRID_SIZE);
        
        if (x >= 0 && x < GRID_COLS && y >= 0 && y < GRID_ROWS) {
            return { x, y };
        }
        return null;
    };

    const handleMouseDown = (e: React.MouseEvent) => {
        const pos = getGridPosition(e.clientX, e.clientY);
        if (!pos) return;

        const blockKey = `${pos.x},${pos.y}`;
        const existingBlock = placedBlocks.get(blockKey);

        setIsMouseDown(true);
        
        if (existingBlock) {
            // Erase mode
            setDragMode('erase');
            const newBlocks = new Map(placedBlocks);
            newBlocks.delete(blockKey);
            setPlacedBlocks(newBlocks);
            onBlocksChange(Array.from(newBlocks.values()));
        } else if (selectedTexture) {
            // Place mode
            setDragMode('place');
            const newBlocks = new Map(placedBlocks);
            newBlocks.set(blockKey, { x: pos.x, y: pos.y, texture: selectedTexture });
            setPlacedBlocks(newBlocks);
            onBlocksChange(Array.from(newBlocks.values()));
        }
    };

    const handleMouseMove = (e: React.MouseEvent) => {
        if (!isMouseDown || !dragMode) return;
        
        const pos = getGridPosition(e.clientX, e.clientY);
        if (!pos) return;

        const blockKey = `${pos.x},${pos.y}`;
        const newBlocks = new Map(placedBlocks);

        if (dragMode === 'place' && selectedTexture) {
            newBlocks.set(blockKey, { x: pos.x, y: pos.y, texture: selectedTexture });
        } else if (dragMode === 'erase') {
            newBlocks.delete(blockKey);
        }

        setPlacedBlocks(newBlocks);
        onBlocksChange(Array.from(newBlocks.values()));
    };

    const handleMouseUp = () => {
        setIsMouseDown(false);
        setDragMode(null);
    };

    const clearCanvas = () => {
        setPlacedBlocks(new Map());
        onBlocksChange([]);
    };

    const renderGrid = () => {
        const grid = [];
        for (let y = 0; y < GRID_ROWS; y++) {
            for (let x = 0; x < GRID_COLS; x++) {
                grid.push(
                    <div
                        key={`${x},${y}`}
                        className="grid-cell"
                        data-x={x}
                        data-y={y}
                        style={{
                            left: x * GRID_SIZE,
                            top: y * GRID_SIZE
                        }}
                    />
                );
            }
        }
        return grid;
    };

    return (
        <div className="block-canvas-container">
            <div className="canvas-controls">
                <h3>Block Canvas</h3>
                <button onClick={clearCanvas} className="clear-button">
                    Clear All
                </button>
            </div>
            
            <div
                ref={canvasRef}
                className="block-canvas"
                data-width={CANVAS_WIDTH}
                data-height={CANVAS_HEIGHT}
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseUp}
            >
                {renderGrid()}
                
                {Array.from(placedBlocks.values()).map((block) => (
                    <div
                        key={`block-${block.x}-${block.y}`}
                        className="placed-block"
                        data-x={block.x}
                        data-y={block.y}
                        style={{
                            left: block.x * GRID_SIZE,
                            top: block.y * GRID_SIZE
                        }}
                    >
                        <img
                            src={`/textures/${block.texture}`}
                            alt="block"
                            className="block-image"
                            draggable={false}
                        />
                    </div>
                ))}
            </div>
            
            <div className="canvas-info">
                <p>Left click to place blocks, right click or click on existing blocks to erase</p>
                <p>Blocks placed: {placedBlocks.size}</p>
            </div>
        </div>
    );
};

export default BlockCanvas;